import type { AuthModalOverrides } from 'src/types';
import classnames from 'classnames';
import styles from './OverrideModal.module.less';
import { Button } from '@nerdwallet/currency';
import CloseButton from './auth/buttons/CloseButton';
import { colors } from '@nerdwallet/base-styles/variables';
import { useEffect, useState } from 'preact/hooks';
import SegmentImpression, {
  ImpressionType,
} from './lib/segment/SegmentImpression';
import * as uuid from 'uuid';
import useAnalytics from './lib/segment/useAnalytics';
import { isEmpty } from './util/helpers';
import { Text } from './auth/components/text/Text';
import { cookieExists } from './auth/lib/cookie-utils';
interface Props {
  authModalOverride: AuthModalOverrides;
}

const modalOverrideCookieSuffix = 'dismissal-cookie';

const OverrideModal: React.FC<Props> = ({ authModalOverride }) => {
  if (!authModalOverride || isEmpty(authModalOverride)) return null;

  useEffect(() => {
    if (authModalOverride.modalName) {
      const modalOverrideCookieName = `${authModalOverride.modalName}-${modalOverrideCookieSuffix}`;

      if (cookieExists(modalOverrideCookieName)) {
        return null;
      }

      let expireDate;
      if (authModalOverride.dismissAfter === null) {
        // 400 days expiration
        const fourHundredDaysInTheFuture =
          Date.now() + 1000 * 60 * 60 * 24 * 400;
        expireDate = new Date(fourHundredDaysInTheFuture).toUTCString();
      } else {
        const dismissAfter = Math.max(authModalOverride.dismissAfter ?? 0, 0);
        expireDate = new Date(Date.now() + dismissAfter * 1000).toUTCString();
      }
      document.cookie = `${modalOverrideCookieName}=true; expires=${expireDate}; path=/`;
    }
  }, [authModalOverride.modalName]);

  const segment = useAnalytics();
  const [segmentImpressionId] = useState(uuid.v4());

  const [showOverrideModal, setShowOverrideModal] = useState(true);
  const closeModal = () => {
    setShowOverrideModal(false);
  };

  const handleClick = (segmentImpressionId) => {
    const eventProps = {
      segmentAttributes: {
        entity_name: 'global_nav_flexible_modal_cta',
        parent_event_id: segmentImpressionId,
      },
    };
    segment.track('link_clicked', eventProps);
  };

  const checkBackgroundColor = (backgroundColor) => {
    if (colors[backgroundColor]) {
      return colors[backgroundColor];
    }
    return colors['blue-lightest'];
  };

  const isNotEmpty = (authModalOverride) => {
    return (
      authModalOverride?.title ||
      authModalOverride?.body ||
      authModalOverride?.ctaText ||
      authModalOverride?.ctaURL
    );
  };

  return (
    showOverrideModal &&
    isNotEmpty(authModalOverride) && (
      <div
        class="modal"
        className={classnames(styles.modalWrapper)}
        id="override-modal-wrapper"
        style={{
          backgroundColor: checkBackgroundColor(
            authModalOverride?.backgroundColor
          ),
        }}
      >
        <div className={classnames(styles.closeButton)}>
          <CloseButton onClick={closeModal} />
        </div>
        <div className={classnames(styles.modalContent)}>
          {authModalOverride?.title && (
            <Text component="h4">{authModalOverride?.title}</Text>
          )}
          {authModalOverride?.body && (
            <div className={classnames(styles.bodyText)}>
              <Text component="p" size="default">
                {authModalOverride?.body}
              </Text>
            </div>
          )}
        </div>
        {authModalOverride?.ctaURL && authModalOverride?.ctaText && (
          <div className={classnames(styles.modalButton)}>
            <SegmentImpression
              eventName="link_viewed"
              eventType={ImpressionType.TRACK}
              eventProps={{
                segmentAttributes: {
                  entity_name: 'global_nav_flexible_modal_cta',
                  destination_url: authModalOverride?.ctaURL,
                  link_copy: authModalOverride?.ctaText,
                  link_position: 0,
                  impression_id: segmentImpressionId,
                },
              }}
            >
              <Button
                primary
                block
                href={authModalOverride?.ctaURL.toString()}
                onClick={() => handleClick(segmentImpressionId)}
              >
                {authModalOverride?.ctaText}
              </Button>
            </SegmentImpression>
          </div>
        )}
      </div>
    )
  );
};

export default OverrideModal;
